import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import DisplayAPIsException from "../../../../BasicComponents/DisplayAPIsExceptionAlert";
import { logEvent, logException } from "../../../../helper/AppInsightsHelper";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import { PRODUCT_VIEW_DATA } from "../../Common/ProductView.constants";
import { useGetCOSOCDataByProductIdQuery } from "../../../../slices/ExploreProducts.slice";
import WarningBanner from "../../../../helper/WarningBanner";
import COSOCTable from "../COSOCCommunity/COSOCTable";
import COSOCChart from "../COSOCCommunity/COSOCChart";

export const COSOCDataTab = () => {
  // Properties
  const [searchParams] = useSearchParams();
  const tenantId = searchParams.get("id");
  const [filteredItems, setFilteredItems] = useState([]);
  const [currentFilter, setCurrentFilter] = useState({
    filterKey: "",
    value: ""
  });
  const [chartFilters, setChartFilters] = React.useState<{ [key: string]: any }>({
    endOfMonth: "",
    stackOverflowSource: ""
  });
  const productId: any = searchParams.get("productid") ?? PRODUCT_VIEW_DATA.PRODUCT_VIEW.DEFAULT_PRODUCT_ID;
  const productName: any = searchParams.get("productname") ?? PRODUCT_VIEW_DATA.PRODUCT_VIEW.DEFAULT_PRODUCT_NAME;
  const { data, error, isFetching } = useGetCOSOCDataByProductIdQuery(productId);
  const customProps = {
    productName: productName
  };

  // Hooks
  useEffect(() => {
    logEvent("Community", "Product View Page", "COSOCDataTab", customProps);
  }, []);

  useEffect(() => {
    setFilteredItems(data?.cosocCommunityList ?? []);

    if (error) {
      logException("Product View Page - Community Tab", "COSOCDataTab", "useGetCOSOCDataByProductIdQuery", error);
    }
  }, [data, error]);

  useEffect(() => {
    const filteredData = (data?.cosocCommunityList || []).filter(({ stackOverflowSource, endOfMonth }: any) => {
      const filters: { [key: string]: any } = {
        ...chartFilters,
        [currentFilter.filterKey]: currentFilter.value
      };
      if (!currentFilter.filterKey) {
        return true;
      }
      return (
        (filters.endOfMonth === undefined || filters.endOfMonth === endOfMonth) &&
        (filters.stackOverflowSource === undefined || filters.stackOverflowSource === stackOverflowSource)
      );
    });
    setFilteredItems(filteredData);
    setChartFilters(() => {
      if (!currentFilter.filterKey) {
        return {
          endOfMonth: undefined,
          stackOverflowSource: undefined
        };
      }
      return {
        ...chartFilters,
        [currentFilter.filterKey]: currentFilter.value
      };
    });
  }, [currentFilter, data]);

  const showNoData = () => {
    const rootStyle = {
      alignItems: "center",
      display: "flex",
      fontSize: 18,
      fontWeight: 600,
      justifyContent: "center",
      padding: 200
    };
    return <div style={rootStyle}>{"COSOC Community Data is not available for " + productName + "."}</div>;
  };

  if (!isFetching && error) {
    return <DisplayAPIsException error={error} height={250} />;
  }

  return (
    <>
      {!isFetching && !error && filteredItems?.length > 0 && (
        <WarningBanner message="Note: COSOC Community view does not support filtering yet. Top filters won't filter the data in this view. Stay tuned for more updates!" />
      )}

      {isFetching || (!error && Array.isArray(data?.cosocCommunityList) && data?.cosocCommunityList.length) ? (
        <COSOCChart
          loading={isFetching}
          onSelectedChartFilter={setCurrentFilter}
          tenantId={tenantId}
          filteredData={filteredItems}
          originalData={data}
          tenantName=""
        />
      ) : (
        ""
      )}

      {isFetching ? (
        <CustomShimmer columnCount={11} />
      ) : (
        (!error && Array.isArray(data?.cosocCommunityList) && data?.cosocCommunityList.length && (
          <div style={{ marginLeft: 3, marginRight: 3 }}>
            <COSOCTable
              tableData={filteredItems}
              productId={productId}
              productName={productName}
              kustoLink={data?.cosocCommunityListKustoLink}
            />
          </div>
        )) ||
        showNoData()
      )}
    </>
  );
};
